import Auth from '@aws-amplify/auth'
import { link } from '@src/graphql/http-link'
import { accessToken } from '@utils/access-token'
import { reportError } from '@utils/report-error'
import { hasAnyRole } from '@utils/roles'
import { can, permissionsOf, siteScopeOf } from '@utils/permissions'

// const logger = new Logger('Auth Store')
export const state = {
  currentUser: getSavedState('auth.currentUser'),
  loginUser: () => {
    let user = getSavedState('auth.loginUser')
    return user
  },
  userData: () => {
    let userData = {}
    try {
      userData = getSavedState('auth.userData')
      return userData
    } catch {
      userData = {
        roles: ['CLIENT'],
      }
      return userData
    }
  },
}

export const mutations = {
  SET_CURRENT_USER(state, newValue) {
    state.currentUser = newValue
    saveState('auth.currentUser', newValue)
    // setDefaultAuthHeaders(state)
  },
  SET_LOGIN_USER(state, newValue) {
    state.loginUser = newValue
    saveState('auth.loginUser', newValue)
    // setDefaultAuthHeaders(state)
  },
  SET_USER_DATA(state, newValue) {
    state.userData = newValue
    saveState('auth.userData', newValue)
  },
}

export const getters = {
  // Whether the user is currently logged in.
  loggedIn(state) {
    return !!state.currentUser
  },
  email(state) {
    if (
      state.currentUser &&
      state.currentUser.idToken &&
      state.currentUser.idToken.payload
    ) {
      return state.currentUser.idToken.payload.email
    }
    return ''
  },
  // The app's role predicates live here so every consumer agrees. Components
  // pull them in via `authComputed` / `userComputed` (@state/helpers) rather
  // than re-implementing the check, see src/utils/roles.js for why.
  //
  // Whether the user holds an admin-capable role (ADMIN or the internal
  // DEVELOPER role), the same set the nav bar uses to show the Admin menu, so
  // route gating and visible navigation always agree. Admin access is managed
  // through the ROLE system (roles.vue / addRoleToUser); this getter previously
  // read the legacy Cognito `admin` group, which the app no longer maintains.
  // It stays a getter because the router's `adminRequired` guard reads it.
  isAdmin(state) {
    return hasAnyRole(
      state.userData && state.userData.roles,
      'ADMIN',
      'DEVELOPER'
    )
  },
  // Adds AGENT: field/technician access (infrared, surveys) on top of admin.
  isAdminOrAgent(state) {
    return hasAnyRole(
      state.userData && state.userData.roles,
      'ADMIN',
      'AGENT',
      'DEVELOPER'
    )
  },
  // DEVELOPER only. An ADMIN is deliberately NOT a developer. Gates the
  // "super user" role-granting rights.
  isDeveloper(state) {
    return hasAnyRole(state.userData && state.userData.roles, 'DEVELOPER')
  },
  // Module permissions (src/utils/permissions.js): `can('loto.write')`. ADMIN
  // and DEVELOPER hold all of them. Read as a function so a template can ask
  // about any permission: `v-if="can('infrared.reports')"`.
  can(state) {
    return (permission) => can(state.userData, permission)
  },
  // Every permission the user holds, implied reads included.
  permissions(state) {
    return permissionsOf(state.userData)
  },
  // ALL or ASSIGNED: whether the user's permissions reach every site or only
  // the ones they are given.
  siteScope(state) {
    return siteScopeOf(state.userData)
  },
  roles(state) {
    if (state.userData && state.userData.roles) {
      return state.userData.roles
    }
  },
  id(state) {
    if (state.userData && state.userData.id) {
      return state.userData.id
    }
  },
  accountLocations(state) {
    if (state.userData && state.userData.roles) {
      return state.userData.accountLocations
    }
  },
  // From /me, not from the Cognito token: both are ours, not part of the
  // identity. Empty string rather than undefined so a template can render them
  // without guarding.
  company(state) {
    return (state.userData && state.userData.company) || ''
  },
  title(state) {
    return (state.userData && state.userData.title) || ''
  },
}

export const actions = {
  // This is automatically run in `src/state/store.js` when the app
  // starts, along with any other actions named `init` in other modules.
  init({ state, dispatch }) {
    // setDefaultAuthHeaders(state)
    dispatch('validate')
      .then(() => {
        return true
      })
      .catch(() => {
        return false
      })
  },

  async saveLoginUserDetails({ commit }, { user } = {}) {
    commit('SET_LOGIN_USER', user)
  },
  async logIn({ commit, dispatch, getters }, { email, password } = {}) {
    try {
      if (getters.loggedIn) {
        const user = dispatch('validate')
        return user
      }
      const user = await Auth.signIn(email, password)
      if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
        return user
      }
      commit('SET_CURRENT_USER', user.getSignInUserSession())
      if (user.getSignInUserSession() != null) {
        // A fresh sign-in always fetches: this browser may hold a previous
        // user's cache entry, and though the new token would miss it anyway,
        // forcing says so plainly.
        const userData = await getMe({ force: true })
        commit('SET_USER_DATA', userData)
      } else {
        const userData = {
          roles: ['CLIENT'],
        }
        commit('SET_USER_DATA', userData)
      }
      return user
    } catch (err) {
      throw err
    }
  },
  async forgotPassword(_, { email }) {
    try {
      const data = await Auth.forgotPassword(email)
      return data
    } catch (err) {
      throw err
    }
  },
  async submitForgotPassword(_, { email, code, newPassword }) {
    try {
      await Auth.forgotPasswordSubmit(email, code, newPassword)
      return true
    } catch (err) {
      throw err
    }
  },
  async changePassword(_, { oldPassword, newPassword }) {
    try {
      const user = await Auth.currentAuthenticatedUser()
      const data = await Auth.changePassword(user, oldPassword, newPassword)
      return data
    } catch (err) {
      throw err
    }
  },
  async newPasswordChallenge({ commit }, { password, user, attributes } = {}) {
    // Falsy rather than `=== null`: the caller passes form fields, so an empty
    // password (or a missing login user) has to be caught here too.
    if (!password || !user || !attributes) {
      throw new Error('Must complete all the entries.')
    }
    try {
      // MUST be awaited. This used to be a bare `.then(...)`, so the action
      // resolved before Cognito answered. New-password.vue's `.then()` then
      // announced "Password was successfully reset" and navigated away even
      // when the challenge had failed, and the rejection surfaced only as an
      // unhandled promise rejection.
      const u = await Auth.completeNewPassword(user, password, attributes)
      commit('SET_CURRENT_USER', u.getSignInUserSession())
      commit('SET_LOGIN_USER', null)
      return u
    } catch (err) {
      // Rethrow so the caller can show `err.message`. (There used to be a
      // `dispatch('errors/setErrorMessage', ...)` here, but the `errors` module
      // was removed in 2018 in favour of `messages`.)
      commit('SET_LOGIN_USER', null)
      throw err
    }
  },

  // Logs out the current user.
  async logOut({ commit, dispatch }) {
    try {
      await Auth.signOut()
    } finally {
      commit('SET_CURRENT_USER', null)
      commit('SET_LOGIN_USER', null)
      commit('SET_USER_DATA', null)
      // Drop the cached /me so the next user on this browser cannot be served
      // the previous user's identity from it.
      clearMeCache()
      // Clear the persisted chat so it doesn't carry over to the next user on
      // this browser (the other way it clears is the End Session button).
      dispatch('chat/resetChat', null, { root: true })
    }
  },

  // Validates the current user's token and refreshes it
  // with new data from the API.
  //
  // `force` bypasses the /me cache. Pass it right after the user changes
  // something on their own profile so the re-read reflects the change at once
  // rather than after the cache window.
  async validate({ commit, state }, { force = false } = {}) {
    // Offline short-circuit: with no connection we can reach neither Cognito
    // (to refresh the token) nor the backend (/me). Trust the persisted session
    // instead of attempting network calls that would reject and hang the router
    // guard. The cached session still drives `loggedIn` (ID-token claims) and
    // `isAdmin` (persisted userData roles).
    //
    // This is safe because client-side gating is a UX affordance, not a
    // security boundary: every real read/write still carries the token to the
    // server, which remains the true gate and rejects a genuinely invalid one.
    // The worst case here is read-only access to data already cached on this
    // device. On reconnect the next validate() does a real refresh and will
    // bounce a dead session to login.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      return state.currentUser
    }
    try {
      const user = await Auth.currentAuthenticatedUser()
      if (user.getSignInUserSession() != null) {
        commit('SET_CURRENT_USER', user.getSignInUserSession())
        const userData = await getMe({ force })
        commit('SET_USER_DATA', userData)
        return user
      }
      const data = await new Promise((resolve, reject) => {
        user.getSession(function (err, data) {
          if (err) {
            reject(err)
          }
          resolve(user)
        })
      })
      commit('SET_CURRENT_USER', data)
      const userData = await getMe({ force })
      commit('SET_USER_DATA', userData)
      return user
    } catch (err) {
      const credentials = await Auth.currentCredentials()
      if (credentials.statusCode === 400) {
        commit('SET_CURRENT_USER', null)
        throw err
      }
      try {
        const user = await Auth.currentAuthenticatedUser()
        commit('SET_CURRENT_USER', user.getSignInUserSession())
        const userData = await getMe({ force })
        commit('SET_USER_DATA', userData)
        return user
      } catch (err) {
        commit('SET_CURRENT_USER', null)
        throw err
      }
    }
  },
}

// ===
// Private helpers
// ===

// Client-side /me cache.
//
// validate() runs on EVERY route change (the router guard) and awaits getMe(),
// so without this each navigation blocked on a network round-trip for data that
// rarely changes between clicks, the page couldn't render until /me answered.
//
// Keyed by the access token. Auth.currentSession() hands back the same cached
// token until it actually expires (~hourly), so the key is stable across
// navigation and only changes on a genuine refresh, which is exactly when a
// re-validate is wanted anyway, so a token change correctly forces a fetch.
//
// The TTL bounds staleness the way the backend user cache does. It only affects
// how quickly a SERVER-side change with no token change (an admin granting a
// role, disabling an account) reaches the UI; client-side gating is a UX
// affordance and the server re-checks every real request, the same trade the
// rest of this module already makes. A change the USER makes to their own
// profile is reflected at once via force below, not by waiting out the window.
const ME_CACHE_TTL_MS = 10_000
let meCache = null // { token, data, expiresAt }
let meInFlight = null // { token, promise }. Collapses concurrent callers
// Monotonic ticket per fetch: a slow older request must not overwrite the cache
// a newer (e.g. forced) one already wrote, or a just-saved profile change could
// be re-hidden by an in-flight navigation fetch that predated the save.
let meFetchSeq = 0

// clearMeCache drops the cached identity. Called on logout so the next user on
// this browser cannot be served the previous user's /me.
export function clearMeCache() {
  meCache = null
  meInFlight = null
}

// getMe fetches the current user from /me, served from a short-lived cache.
//
// force skips the cache (both the stored entry and any in-flight request) and
// always fetches fresh. Used right after a self-service profile edit so the
// change shows immediately rather than after the TTL.
//
// Errors are never cached: a failed fetch returns undefined (as before) and
// leaves the cache untouched, so the next call retries.
async function getMe({ force = false } = {}) {
  // Goes through accessToken() like every other authenticated call rather than
  // reading state directly: validate() commits the session and then calls this, so
  // the stored token is normally fresh, but getMe is also reachable on the offline
  // and retry paths where it is not.
  const token = await accessToken(state.currentUser?.accessToken?.jwtToken)

  if (
    !force &&
    meCache &&
    meCache.token === token &&
    meCache.expiresAt > Date.now()
  ) {
    return meCache.data
  }
  // Two guards firing at once (or init() racing the first navigation) would
  // otherwise each round-trip. A forced call never joins an in-flight one. That
  // request may predate the change force is meant to pick up.
  if (!force && meInFlight && meInFlight.token === token) {
    return meInFlight.promise
  }

  const seq = ++meFetchSeq
  const promise = (async () => {
    let data
    try {
      const res = await fetch(link['bowvue_base_uri'] + '/me', {
        method: 'GET',
        withCredentials: true,
        headers: {
          Authorization: 'Bearer ' + token,
          'Content-Type': 'application/json',
        },
      })
      data = await res.json()
    } catch (error) {
      // Same as before: report and resolve undefined, without caching.
      return reportError(error, { source: 'auth', operation: 'getMe' })
    }
    // Only the most-recently-STARTED fetch may write, so a slow older request
    // can't clobber a fresher entry.
    if (seq === meFetchSeq) {
      meCache = { token, data, expiresAt: Date.now() + ME_CACHE_TTL_MS }
    }
    return data
  })()

  if (!force) {
    meInFlight = { token, promise }
  }
  try {
    return await promise
  } finally {
    if (meInFlight && meInFlight.promise === promise) {
      meInFlight = null
    }
  }
}

function getSavedState(key) {
  return JSON.parse(window.localStorage.getItem(key))
}

function saveState(key, state) {
  window.localStorage.setItem(key, JSON.stringify(state))
}

// function setDefaultAuthHeaders(state) {
//   // axios.defaults.headers.common.Authorization = state.currentUser
//   //   ? state.currentUser.jwtToken
//   //   : ''
// }
